/**
 * Labstep
 */

import { EntityBatchProcessCreateContainer } from 'labstep-web/containers/Entity/BatchProcessCreate';
import { EntityCanCreateContainer } from 'labstep-web/containers/Entity/Create/Can';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { Icon } from 'labstep-web/core/Icon';
import { AmountUpdate } from 'labstep-web/models/amount-update.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { isAmountUpdated } from 'labstep-web/services/amount-unit.service';
import {
  evaluateProtocolValue,
  getValidProtocolValues,
} from 'labstep-web/services/protocol-value.service';
import type React from 'react';
import type { ProtocolValueActionBulkUpdateAmountsProps } from './types';

export const ProtocolValueActionBulkUpdateAmounts: React.FC<
  ProtocolValueActionBulkUpdateAmountsProps
> = ({ protocolValues, onSuccess }) => {
  const gridSmartTableProtocolConditionService =
    useGridSmartTableProtocolConditionServiceFromContext();
  const amountsNotUpdated = protocolValues.some(
    (p: ProtocolValue) => !isAmountUpdated(p),
  );

  const handleUpdateAmounts = (
    create: (items: any[], options: any) => void,
  ) => {
    gridSmartTableProtocolConditionService?.agGrid.api.refreshCells({
      force: true,
      suppressFlash: true,
    });
    gridSmartTableProtocolConditionService?.agGrid.api.redrawRows();
    const filteredProtocolValues: ProtocolValue[] = [];
    protocolValues.forEach((protocolValue: ProtocolValue) => {
      const { canUpdate } = evaluateProtocolValue(protocolValue);

      if (canUpdate) {
        filteredProtocolValues.push(protocolValue);
      }
    });
    const validProtocolValues = getValidProtocolValues(
      filteredProtocolValues,
    );

    const items = validProtocolValues.map((value) => ({
      protocol_value_id: value.protocolValueId,
    }));

    if (items.length > 0) {
      create(items, {
        onSuccess: onSuccess,
        onEnd: () => {
          // gets called after every batch is processed
          gridSmartTableProtocolConditionService?.agGrid.api.refreshHeader();
        },
      });
    } else {
      onSuccess?.();
    }
  };

  return (
    <EntityCanCreateContainer entityName={AmountUpdate.entityName}>
      {() => (
        <EntityBatchProcessCreateContainer
          entityName={AmountUpdate.entityName}
          title="Updating amounts..."
          maxBatchSize={2}
        >
          {({ create, batchStatuses }) => (
            <Icon
              disabled={
                batchStatuses &&
                batchStatuses.some(
                  (batchStatuses) => batchStatuses.status?.isFetching,
                )
              }
              color={amountsNotUpdated ? 'yellow' : 'green'}
              name="check circle"
              data-testid="conditions_table_item_input"
              popup={{
                content: amountsNotUpdated
                  ? 'Update all amounts'
                  : 'All Amounts Updated',
                size: 'small',
              }}
              onClick={
                amountsNotUpdated
                  ? () => handleUpdateAmounts(create)
                  : undefined
              }
            />
          )}
        </EntityBatchProcessCreateContainer>
      )}
    </EntityCanCreateContainer>
  );
};
